import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import axios from 'axios';
import Select from 'react-select';

const GLJournalsPage = () => {
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState('manual');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Manual Entry Form State
    const [manualLines, setManualLines] = useState([{ accountCode: null, debitAmount: '', creditAmount: '' }]);
    const [manualNarration, setManualNarration] = useState('');
    const [totalDebits, setTotalDebits] = useState(0);
    const [totalCredits, setTotalCredits] = useState(0);
    const [chartOfAccounts, setChartOfAccounts] = useState([]);
    const [recentEntries, setRecentEntries] = useState([]);

    // Unposted Transactions State
    const [unpostedTransactions, setUnpostedTransactions] = useState([]);
    const [unpostedPage, setUnpostedPage] = useState(1);
    const [, setUnpostedTotal] = useState(0);
    const [unpostedSelected, setUnpostedSelected] = useState(new Set());
    const [unpostedFilters] = useState({ ownerCode: '', analysisCode: '' });

    // GL Entries State
    const [glEntries, setGlEntries] = useState([]);
    const [glEntriesPage, setGlEntriesPage] = useState(1);
    const [, setGlEntriesTotal] = useState(0);
    const [glEntriesFilters] = useState({ accountCode: '', referenceType: '' });
    const [reverseReason, setReverseReason] = useState('');
    const [reverseEntryId, setReverseEntryId] = useState(null);

    const LIMIT = 20;

    // Fetch Chart of Accounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchChartOfAccounts();
    }, []);

    const fetchChartOfAccounts = async () => {
        try {
            const response = await axios.get(`${rootUrl}/system/coa/accounts`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                const options = response.data.data
                    .filter(acc => acc.is_enabled)
                    .map(acc => ({
                        value: acc.account_code,
                        label: `${acc.account_code} - ${acc.account_name}`
                    }));
                setChartOfAccounts(options);
            }
        } catch (error) {
            console.error('Error fetching chart of accounts:', error);
        }
    };

    // Fetch data based on active tab
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (activeTab === 'manual') {
            fetchRecentEntries();
        } else if (activeTab === 'unposted') {
            fetchUnpostedTransactions();
        } else if (activeTab === 'entries') {
            fetchGLEntries();
        }
    }, [activeTab, unpostedPage, glEntriesPage]);

    const fetchRecentEntries = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${rootUrl}/api/gl/entries`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { referenceType: 'MANUAL', limit: 10, page: 1 }
            });
            if (response.data.success) {
                setRecentEntries(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching recent entries:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUnpostedTransactions = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${rootUrl}/api/gl/unposted`, {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    ...unpostedFilters,
                    page: unpostedPage,
                    limit: LIMIT
                }
            });
            if (response.data.success) {
                setUnpostedTransactions(response.data.data);
                setUnpostedTotal(response.data.total);
            }
        } catch (error) {
            console.error('Error fetching unposted transactions:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchGLEntries = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${rootUrl}/api/gl/entries`, {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    ...glEntriesFilters,
                    page: glEntriesPage,
                    limit: LIMIT
                }
            });
            if (response.data.success) {
                setGlEntries(response.data.data);
                setGlEntriesTotal(response.data.total);
            }
        } catch (error) {
            console.error('Error fetching GL entries:', error);
        } finally {
            setLoading(false);
        }
    };

    // Manual Entry Handlers
    const handleLineChange = (index, field, value) => {
        const newLines = [...manualLines];
        newLines[index][field] = value;
        setManualLines(newLines);
        calculateTotals(newLines);
    };

    const calculateTotals = (lines) => {
        let debits = 0,
            credits = 0;
        lines.forEach(line => {
            if (line.debitAmount) debits += parseFloat(line.debitAmount) || 0;
            if (line.creditAmount) credits += parseFloat(line.creditAmount) || 0;
        });
        setTotalDebits(debits);
        setTotalCredits(credits);
    };

    const addLine = () => {
        setManualLines([...manualLines, { accountCode: null, debitAmount: '', creditAmount: '' }]);
    };

    const removeLine = (index) => {
        if (manualLines.length > 1) {
            const newLines = manualLines.filter((_, i) => i !== index);
            setManualLines(newLines);
            calculateTotals(newLines);
        }
    };

    const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;

    const submitManualEntry = async () => {
        if (!isBalanced) {
            setMessage({ type: 'error', text: 'Debits and credits must balance' });
            return;
        }

        if (!manualNarration.trim()) {
            setMessage({ type: 'error', text: 'Narration is required' });
            return;
        }

        try {
            setLoading(true);
            const lines = manualLines.map(line => ({
                accountCode: line.accountCode?.value || line.accountCode,
                debitAmount: parseFloat(line.debitAmount) || 0,
                creditAmount: parseFloat(line.creditAmount) || 0
            }));

            const response = await axios.post(`${rootUrl}/api/gl/entries/manual`,
                { lines, narration: manualNarration },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setMessage({ type: 'success', text: response.data.message });
                setManualLines([{ accountCode: null, debitAmount: '', creditAmount: '' }]);
                setManualNarration('');
                setTotalDebits(0);
                setTotalCredits(0);
                fetchRecentEntries();
                setTimeout(() => setMessage({ type: '', text: '' }), 3000);
            } else {
                setMessage({ type: 'error', text: response.data.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Error posting GL entry' });
        } finally {
            setLoading(false);
        }
    };

    // Unposted Transactions Handlers
    const toggleUnpostedSelect = (transId) => {
        const newSelected = new Set(unpostedSelected);
        if (newSelected.has(transId)) {
            newSelected.delete(transId);
        } else {
            newSelected.add(transId);
        }
        setUnpostedSelected(newSelected);
    };

    const postUnpostedBatch = async () => {
        if (unpostedSelected.size === 0) {
            setMessage({ type: 'error', text: 'Select transactions to post' });
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(`${rootUrl}/api/gl/post-batch`,
                { transactionIds: Array.from(unpostedSelected) },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setMessage({ type: 'success', text: response.data.message });
                setUnpostedSelected(new Set());
                fetchUnpostedTransactions();
                setTimeout(() => setMessage({ type: '', text: '' }), 3000);
            } else {
                setMessage({ type: 'error', text: response.data.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Error posting transactions' });
        } finally {
            setLoading(false);
        }
    };

    // GL Entries Handlers
    const reverseEntry = async () => {
        if (!reverseReason.trim()) {
            setMessage({ type: 'error', text: 'Reversal reason is required' });
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(`${rootUrl}/api/gl/entries/${reverseEntryId}/reverse`,
                { reason: reverseReason },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setMessage({ type: 'success', text: 'GL entry reversed successfully' });
                setReverseEntryId(null);
                setReverseReason('');
                fetchGLEntries();
                setTimeout(() => setMessage({ type: '', text: '' }), 3000);
            } else {
                setMessage({ type: 'error', text: response.data.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Error reversing entry' });
        } finally {
            setLoading(false);
        }
    };

    // Format date utility
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toFixed(2);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">GL Journals</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">Manage manual GL entries and owner transaction postings</p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                {/* Message Display */}
                {message.text && (
                    <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {message.text}
                    </div>
                )}

                {/* Tab Buttons */}
                <div className="flex border-b border-gray-200 mb-6 bg-white rounded-t-2xl">
                    <button
                        onClick={() => setActiveTab('manual')}
                        className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'manual'
                            ? 'border-[#FF5733] text-[#FF5733]'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        Manual Entries
                    </button>
                    <button
                        onClick={() => setActiveTab('unposted')}
                        className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'unposted'
                            ? 'border-[#FF5733] text-[#FF5733]'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        Unposted Transactions
                    </button>
                    <button
                        onClick={() => setActiveTab('entries')}
                        className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'entries'
                            ? 'border-[#FF5733] text-[#FF5733]'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        GL Entries
                    </button>
                </div>

                {/* Tab Content */}
                <div className="bg-white rounded-b-2xl shadow-lg p-6">
                    {/* Manual Entries Tab */}
                    {activeTab === 'manual' && (
                        <div>
                            <h2 className="text-2xl font-bold text-[#2a2e30] mb-6">Create Manual GL Entry</h2>

                            {/* Entry Form */}
                            <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="mb-6">
                                    <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Narration *</label>
                                    <textarea
                                        value={manualNarration}
                                        onChange={(e) => setManualNarration(e.target.value)}
                                        placeholder="Enter description for this GL entry"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                                        rows={3}
                                    />
                                </div>

                                {/* Line Items Table */}
                                <div className="mb-6 overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gray-200">
                                                <th className="px-4 py-2 text-left font-semibold">Account Code</th>
                                                <th className="px-4 py-2 text-right font-semibold">Debit</th>
                                                <th className="px-4 py-2 text-right font-semibold">Credit</th>
                                                <th className="px-4 py-2 text-center font-semibold">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {manualLines.map((line, index) => (
                                                <tr key={index} className="border-b border-gray-200">
                                                    <td className="px-4 py-3">
                                                        <Select
                                                            options={chartOfAccounts}
                                                            value={
                                                                chartOfAccounts.find(
                                                                    (opt) =>
                                                                        opt.value === (line.accountCode?.value || line.accountCode)
                                                                ) || null
                                                            }
                                                            onChange={(selected) =>
                                                                handleLineChange(index, 'accountCode', selected)
                                                            }
                                                            placeholder="Select account..."
                                                            isClearable
                                                            isSearchable
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={line.debitAmount}
                                                            onChange={(e) => handleLineChange(index, 'debitAmount', e.target.value)}
                                                            placeholder="0.00"
                                                            className="w-full p-2 border border-gray-300 rounded-lg text-right focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={line.creditAmount}
                                                            onChange={(e) => handleLineChange(index, 'creditAmount', e.target.value)}
                                                            placeholder="0.00"
                                                            className="w-full p-2 border border-gray-300 rounded-lg text-right focus:outline-none focus:ring-2 focus:ring-[#FF5733]"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <button
                                                            onClick={() => removeLine(index)}
                                                            className="text-red-600 hover:text-red-800 font-semibold"
                                                            disabled={manualLines.length === 1}
                                                        >
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Totals */}
                                <div className="flex justify-end gap-8 mb-6 p-4 bg-white rounded border border-gray-300">
                                    <div>
                                        <span className="font-semibold text-gray-700">Total Debits:</span>
                                        <span className="ml-2 font-bold text-lg">KES {formatCurrency(totalDebits)}</span>
                                    </div>
                                    <div>
                                        <span className="font-semibold text-gray-700">Total Credits:</span>
                                        <span className="ml-2 font-bold text-lg">KES {formatCurrency(totalCredits)}</span>
                                    </div>
                                    <div>
                                        <span className={`font-bold text-lg ${isBalanced ? 'text-green-600' : 'text-red-600'}`}>
                                            {isBalanced ? '✓ Balanced' : '✗ Unbalanced'}
                                        </span>
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="flex gap-4">
                                    <button
                                        onClick={addLine}
                                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-4 py-2 font-semibold cursor-pointer"
                                    >
                                        + Add Line
                                    </button>
                                    <button
                                        onClick={submitManualEntry}
                                        disabled={!isBalanced || loading}
                                        className={`${isBalanced && !loading
                                            ? 'bg-[#FF5733] hover:bg-[#e64520] cursor-pointer'
                                            : 'bg-gray-300 cursor-not-allowed'
                                            } text-white text-sm rounded-sm px-6 py-2 font-semibold`}
                                    >
                                        {loading ? 'Posting...' : 'Post Entry'}
                                    </button>
                                </div>
                            </div>

                            {/* Recent Entries */}
                            <h3 className="text-xl font-bold text-[#2a2e30] mb-4">Recent Manual Entries</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-200">
                                            <th className="px-4 py-2 text-left font-semibold">Date</th>
                                            <th className="px-4 py-2 text-left font-semibold">Narration</th>
                                            <th className="px-4 py-2 text-right font-semibold">Debit</th>
                                            <th className="px-4 py-2 text-right font-semibold">Credit</th>
                                            <th className="px-4 py-2 text-left font-semibold">Posted By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentEntries.length > 0 ? (
                                            recentEntries.map((entry) => (
                                                <tr key={entry.id} className="border-b border-gray-200 hover:bg-gray-50">
                                                    <td className="px-4 py-3">{formatDate(entry.entry_date)}</td>
                                                    <td className="px-4 py-3">{entry.description}</td>
                                                    <td className="px-4 py-3 text-right">{entry.debit > 0 ? `KES ${formatCurrency(entry.debit)}` : '-'}</td>
                                                    <td className="px-4 py-3 text-right">{entry.credit > 0 ? `KES ${formatCurrency(entry.credit)}` : '-'}</td>
                                                    <td className="px-4 py-3">{entry.posted_by}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="px-4 py-3 text-center text-gray-500">
                                                    No recent entries
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Unposted Transactions Tab */}
                    {activeTab === 'unposted' && (
                        <div>
                            <h2 className="text-2xl font-bold text-[#2a2e30] mb-6">Unposted Owner Transactions</h2>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-200">
                                            <th className="px-4 py-2 text-center font-semibold">
                                                <input
                                                    type="checkbox"
                                                    checked={unpostedSelected.size === unpostedTransactions.length && unpostedTransactions.length > 0}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setUnpostedSelected(new Set(unpostedTransactions.map(t => t.id)));
                                                        } else {
                                                            setUnpostedSelected(new Set());
                                                        }
                                                    }}
                                                />
                                            </th>
                                            <th className="px-4 py-2 text-left font-semibold">Date</th>
                                            <th className="px-4 py-2 text-left font-semibold">Trans ID</th>
                                            <th className="px-4 py-2 text-left font-semibold">Owner</th>
                                            <th className="px-4 py-2 text-left font-semibold">Type</th>
                                            <th className="px-4 py-2 text-right font-semibold">Amount</th>
                                            <th className="px-4 py-2 text-left font-semibold">Analysis Code</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {unpostedTransactions.length > 0 ? (
                                            unpostedTransactions.map((trans) => (
                                                <tr key={trans.id} className="border-b border-gray-200 hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={unpostedSelected.has(trans.id)}
                                                            onChange={() => toggleUnpostedSelect(trans.id)}
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3">{new Date(trans.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                                                    <td className="px-4 py-3">{trans.id}</td>
                                                    <td className="px-4 py-3">{trans.owner_name || trans.owner_code}</td>
                                                    <td className="px-4 py-3">{trans.trans_type}</td>
                                                    <td className="px-4 py-3 text-right">KES {formatCurrency(trans.debit)}</td>
                                                    <td className="px-4 py-3">{trans.analysis_code || '-'}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="px-4 py-3 text-center text-gray-500">
                                                    No unposted transactions
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Post Selected Button */}
                            <div className="mt-6 flex justify-between items-center">
                                <div className="text-sm text-gray-600">
                                    {unpostedSelected.size > 0 && `${unpostedSelected.size} transaction(s) selected`}
                                </div>
                                <button
                                    onClick={postUnpostedBatch}
                                    disabled={unpostedSelected.size === 0 || loading}
                                    className={`${unpostedSelected.size > 0 && !loading
                                        ? 'bg-[#FF5733] hover:bg-[#e64520] cursor-pointer'
                                        : 'bg-gray-300 cursor-not-allowed'
                                        } text-white text-sm rounded-sm px-6 py-2 font-semibold`}
                                >
                                    {loading ? 'Posting...' : 'Post Selected'}
                                </button>
                            </div>

                            {/* Pagination */}
                            <div className="mt-4 flex justify-center gap-2">
                                <button
                                    onClick={() => setUnpostedPage(Math.max(1, unpostedPage - 1))}
                                    disabled={unpostedPage === 1}
                                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                <span className="px-3 py-1">{unpostedPage}</span>
                                <button
                                    onClick={() => setUnpostedPage(unpostedPage + 1)}
                                    disabled={unpostedTransactions.length < LIMIT}
                                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}

                    {/* GL Entries Tab */}
                    {activeTab === 'entries' && (
                        <div>
                            <h2 className="text-2xl font-bold text-[#2a2e30] mb-6">GL Entries Audit Trail</h2>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-200">
                                            <th className="px-4 py-2 text-left font-semibold">Date</th>
                                            <th className="px-4 py-2 text-left font-semibold">Account</th>
                                            <th className="px-4 py-2 text-right font-semibold">Debit</th>
                                            <th className="px-4 py-2 text-right font-semibold">Credit</th>
                                            <th className="px-4 py-2 text-left font-semibold">Narration</th>
                                            <th className="px-4 py-2 text-left font-semibold">Posted By</th>
                                            <th className="px-4 py-2 text-center font-semibold">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {glEntries.length > 0 ? (
                                            glEntries.map((entry) => (
                                                <tr
                                                    key={entry.id}
                                                    className={`border-b border-gray-200 hover:bg-gray-50 ${entry.reversed ? 'opacity-60 line-through' : ''}`}
                                                >
                                                    <td className="px-4 py-3">{formatDate(entry.entry_date)}</td>
                                                    <td className="px-4 py-3">{entry.account_code}</td>
                                                    <td className="px-4 py-3 text-right">{entry.debit > 0 ? `KES ${formatCurrency(entry.debit)}` : '-'}</td>
                                                    <td className="px-4 py-3 text-right">{entry.credit > 0 ? `KES ${formatCurrency(entry.credit)}` : '-'}</td>
                                                    <td className="px-4 py-3 text-sm">{entry.description}</td>
                                                    <td className="px-4 py-3">{entry.posted_by}</td>
                                                    <td className="px-4 py-3 text-center">
                                                        {!entry.reversed ? (
                                                            <button
                                                                onClick={() => setReverseEntryId(entry.id)}
                                                                className="text-red-600 hover:text-red-800 font-semibold text-sm"
                                                            >
                                                                Reverse
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-500 text-sm">Reversed</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="px-4 py-3 text-center text-gray-500">
                                                    No GL entries
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="mt-4 flex justify-center gap-2">
                                <button
                                    onClick={() => setGlEntriesPage(Math.max(1, glEntriesPage - 1))}
                                    disabled={glEntriesPage === 1}
                                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                <span className="px-3 py-1">{glEntriesPage}</span>
                                <button
                                    onClick={() => setGlEntriesPage(glEntriesPage + 1)}
                                    disabled={glEntries.length < LIMIT}
                                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Reversal Modal */}
                {reverseEntryId && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full">
                            <h3 className="text-xl font-bold text-[#2a2e30] mb-4">Reverse GL Entry</h3>
                            <textarea
                                value={reverseReason}
                                onChange={(e) => setReverseReason(e.target.value)}
                                placeholder="Enter reason for reversal"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5733] mb-4"
                                rows={4}
                            />
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={() => {
                                        setReverseEntryId(null);
                                        setReverseReason('');
                                    }}
                                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-4 py-2 font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={reverseEntry}
                                    disabled={loading}
                                    className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm rounded-sm px-4 py-2 font-semibold disabled:opacity-50"
                                >
                                    {loading ? 'Reversing...' : 'Confirm Reversal'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GLJournalsPage;
